import { useEffect, useId, useRef, useState } from 'react';
import { Info, MoreHorizontal, Plus } from 'lucide-react';
import type { Locale } from './types';
import './supplyLibraryToolbar.css';

export interface SupplyLibraryHeaderProps {
  locale: Locale;
  itemCount: number;
  categoryCount: number;
  unassignedCount: number;
  canManageInventory: boolean;
  unassignedSelected: boolean;
  onAddItem: () => void;
  onAddCategory: () => void;
  onShowUnassigned: () => void;
}

export function SupplyLibraryHeader({ locale, itemCount, categoryCount, unassignedCount, canManageInventory, unassignedSelected, onAddItem, onAddCategory, onShowUnassigned }: SupplyLibraryHeaderProps) {
  const es = locale === 'es';
  const helpId = useId(), moreId = useId();
  const [helpOpen, setHelpOpen] = useState(false), [helpPinned, setHelpPinned] = useState(false), [moreOpen, setMoreOpen] = useState(false);
  const helpRef = useRef<HTMLSpanElement>(null), moreRef = useRef<HTMLDivElement>(null), moreButtonRef = useRef<HTMLButtonElement>(null);
  const showUnassigned = canManageInventory && unassignedCount > 0;
  const closeHelp = () => { setHelpOpen(false); setHelpPinned(false); };

  useEffect(() => {
    if (!canManageInventory) setMoreOpen(false);
    if (!showUnassigned) { setHelpOpen(false); setHelpPinned(false); }
  }, [canManageInventory, showUnassigned]);

  useEffect(() => {
    if (!helpOpen && !moreOpen) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!helpRef.current?.contains(target)) { setHelpOpen(false); setHelpPinned(false); }
      if (!moreRef.current?.contains(target)) setMoreOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      setHelpOpen(false); setHelpPinned(false); setMoreOpen(false);
      if (moreOpen) moreButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [helpOpen, moreOpen]);

  return <div className="section-header supply-library-header">
    <div className="supply-library-overview">
      <p className="supply-library-summary">{es ? `${itemCount} ${itemCount === 1 ? 'insumo' : 'insumos'} · ${categoryCount} ${categoryCount === 1 ? 'categoría' : 'categorías'}` : `${itemCount} ${itemCount === 1 ? 'supply' : 'supplies'} · ${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`}</p>
      {showUnassigned && <div className="supply-library-unassigned">
        <button type="button" className="supply-library-unassigned-button" aria-label={es ? 'Ver sin asignar' : 'Show unassigned'} aria-pressed={unassignedSelected} onClick={onShowUnassigned}>{es ? `Sin departamento (${unassignedCount})` : `No department (${unassignedCount})`}</button>
        <span className="supply-library-help" ref={helpRef} onMouseEnter={() => { setMoreOpen(false); setHelpOpen(true); }}
          onMouseLeave={() => { if (!helpPinned && !helpRef.current?.contains(document.activeElement)) setHelpOpen(false); }}
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closeHelp(); }}>
          <button type="button" className="supply-library-icon-button" aria-label={es ? 'Información sobre insumos sin departamento' : 'About supplies without a department'} aria-expanded={helpOpen} aria-describedby={helpOpen ? helpId : undefined}
            onFocus={() => { setMoreOpen(false); setHelpOpen(true); }} onClick={() => { setMoreOpen(false); setHelpPinned(!helpPinned); setHelpOpen(!helpPinned); }}><Info size={16} aria-hidden="true" /></button>
          {helpOpen && <span className="supply-library-help-content" id={helpId} role="tooltip">{es ? 'Solo visibles con acceso a todos los departamentos.' : 'Only visible with access to all departments.'}</span>}
        </span>
      </div>}
    </div>
    {canManageInventory && <div className="button-row supply-library-actions">
      <button type="button" className="primary-button" aria-label={es ? 'Agregar insumo' : 'Add supply'} onClick={onAddItem}><Plus size={16} aria-hidden="true" />{es ? 'Insumo' : 'Supply'}</button>
      <div className="supply-library-more" ref={moreRef} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setMoreOpen(false); }}>
        <button type="button" className="supply-library-icon-button" ref={moreButtonRef} aria-label={es ? 'Más opciones' : 'More options'} aria-expanded={moreOpen} aria-controls={moreId}
          onClick={() => { closeHelp(); setMoreOpen(!moreOpen); }}><MoreHorizontal size={19} aria-hidden="true" /></button>
        {moreOpen && <div id={moreId} className="supply-library-more-content">
          <button type="button" onClick={() => { setMoreOpen(false); moreButtonRef.current?.focus(); onAddCategory(); }}><Plus size={16} aria-hidden="true" />{es ? 'Agregar categoría' : 'Add category'}</button>
        </div>}
      </div>
    </div>}
  </div>;
}
